import { Input } from "@/components/ui/input";
import { TextInputFieldProps } from "./types";
import { useController } from "react-hook-form";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Info } from "lucide-react";

const textInputFieldStyles = {
  container: cva("flex max-w-[400px] flex-col", {
    variants: {
      size: {
        desktop: "gap-1.5",
        dialog: "gap-1",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
  label: cva("font-medium text-gray-900", {
    variants: {
      size: {
        desktop: "text-base",
        dialog: "text-sm",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
  input: cva(
    "w-full rounded-lg font-medium text-gray-900 placeholder:font-normal placeholder:text-gray-400",
    {
      variants: {
        size: {
          desktop: "h-11 px-3.5 py-2.5 text-base placeholder:text-base",
          dialog: "h-9 px-3 py-2 text-sm placeholder:text-sm",
        },
      },
      defaultVariants: {
        size: "desktop",
      },
    },
  ),
};

export type TextInputFieldStyleProps = VariantProps<
  typeof textInputFieldStyles.container
>;

export const TextInputField = ({
  field,
  form,
  stepId,
  size = "desktop",
}: TextInputFieldProps) => {
  const { trigger, watch } = form;
  const [isInitiated, setIsInitiated] = useState(false);
  const fieldsInputs = watch(stepId);
  const isDisplayed = field.showIf ? field.showIf?.(fieldsInputs) : true;
  const key = `${stepId}.${field.id}`;
  const {
    field: { onChange, value },
    fieldState: { error, isDirty },
  } = useController({
    rules: {
      required: isDisplayed ? field.required : false,
      validate: (value) =>
        isDisplayed ? field.validation?.(value, fieldsInputs) : undefined,
    },
    control: form.control,
    name: key,
  });

  useEffect(() => {
    if (!isDisplayed && error) {
      form.clearErrors(key);
      setIsInitiated(false);
    }
  }, [form, key, isDisplayed, error]);

  useEffect(() => {
    if (!isDisplayed && value) {
      onChange("");
      setIsInitiated(false);
    }
  }, [isDisplayed, value, onChange]);

  useEffect(() => {
    if (isDisplayed) {
      setIsInitiated(true);
      trigger(key);
    }
  }, [isDisplayed, key, trigger, isInitiated]);

  return (
    <div
      className={cn(textInputFieldStyles.container({ size }), {
        hidden: !isDisplayed,
      })}
      key={field.id}
    >
      <div className="flex items-center gap-2">
        <label className={textInputFieldStyles.label({ size })}>
          {typeof field.label === "function"
            ? field.label(fieldsInputs)
            : field.label}
        </label>
        {field.tooltip && (
          <BasicTooltip content={field.tooltip}>
            <Info className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700" />
          </BasicTooltip>
        )}
      </div>
      <Input
        onChange={(e) => {
          onChange(e.target.value);
        }}
        readOnly={field.readOnly}
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        type={field.inputType || "text"}
        value={value}
        className={textInputFieldStyles.input({ size })}
        placeholder={
          typeof field.placeholder === "function"
            ? field.placeholder(fieldsInputs)
            : field.placeholder
        }
      />
      {error && isDirty && (
        <p className="text-sm text-red-500">{error.message}</p>
      )}
    </div>
  );
};
